import type {
	ReportAmountsMode,
	ReportAmountsModeRequestDto,
	ReportAmountsModeResponseDto,
	ReportFavoritesOrderRequestDto,
	ReportFavoritesResponseDto,
	ReportLabelRequestDto,
	ReportLabelsResponseDto,
	ReportPreferenceKeyRequestDto,
	ReportPreferencesDto,
	ReportPreferencesResponseDto,
	ReportRecentDto,
	ReportRecentsResponseDto,
} from '@supply-mover-2-0/shared';
import { ApiService } from './ApiService';

/**
 * Frontend service for a user's favorite and recently used reports
 * (PD-16919), shown on the Reporting home page. Every call acts on the
 * signed-in user's own rows.
 *
 * The calls throw on failure rather than returning an error object, so the
 * store that owns the state can put an optimistic change back.
 */
export class ReportPreferencesQueryService {
	/** The user's favorites (in their order) and recently used reports (newest first). */
	public static async getMine(): Promise<ReportPreferencesDto> {
		const response = await ApiService.apiGet<ReportPreferencesResponseDto>(
			'/reporting-preferences/me',
		);

		return response.data;
	}

	/** PD-17424 — save how report money is shown. Resolves to the saved choice. */
	public static async setAmountsMode(amountsMode: ReportAmountsMode): Promise<ReportAmountsMode> {
		const body: ReportAmountsModeRequestDto = { amountsMode };
		const response = await ApiService.apiPut<ReportAmountsModeResponseDto>(
			'/reporting-preferences/amounts',
			body,
		);

		return response.amountsMode;
	}

	/** PD-17425 — name a report for the company (null restores its name). Resolves to every name. */
	public static async setLabel(reportKey: string, label: string | null): Promise<Record<string, string>> {
		const body: ReportLabelRequestDto = { reportKey, label };
		const response = await ApiService.apiPut<ReportLabelsResponseDto>(
			'/reporting-preferences/labels',
			body,
		);

		return response.labels;
	}

	/** Mark a report as a favorite. Resolves to the favorites afterwards. */
	public static async addFavorite(reportKey: string): Promise<Array<string>> {
		const body: ReportPreferenceKeyRequestDto = { reportKey };
		const response = await ApiService.apiPut<ReportFavoritesResponseDto>(
			'/reporting-preferences/favorites',
			body,
		);

		return response.favorites;
	}

	/** Unmark a favorite. Resolves to the favorites afterwards. */
	public static async removeFavorite(reportKey: string): Promise<Array<string>> {
		// A DELETE carries no body, so the key travels in the query string.
		const response = await ApiService.apiDelete<ReportFavoritesResponseDto>(
			`/reporting-preferences/favorites?reportKey=${encodeURIComponent(reportKey)}`,
		);

		return response.favorites;
	}

	/** Put the favorites in a new order. Resolves to the favorites afterwards. */
	public static async reorderFavorites(
		reportKeys: ReadonlyArray<string>,
	): Promise<Array<string>> {
		const body: ReportFavoritesOrderRequestDto = { reportKeys: [...reportKeys] };
		const response = await ApiService.apiPut<ReportFavoritesResponseDto>(
			'/reporting-preferences/favorites/order',
			body,
		);

		return response.favorites;
	}

	/** Record that the user opened a report. Resolves to the recently used reports afterwards. */
	public static async recordView(
		reportKey: string,
	): Promise<Array<ReportRecentDto>> {
		const body: ReportPreferenceKeyRequestDto = { reportKey };
		const response = await ApiService.apiPost<ReportRecentsResponseDto>(
			'/reporting-preferences/views',
			body,
		);

		return response.recents;
	}
}
